/**
 * StrummingEngine.js
 * Simulates guitar physics: Strumming delays and idiomatic voicings.
 */

const GUITAR_TUNING = [40, 45, 50, 55, 59, 64]; // Standard E (E2, A2, D3, G3, B3, E4)

/**
 * Offsets start times of simultaneous notes to simulate a pick sweeping across strings.
 * @param {Array} events - Events for a single instrument (sorted).
 * @param {number} speed - Time in seconds (or beats) for full strum (e.g. 0.05).
 * @param {string} direction - 'down' (low to high), 'up' (high to low), 'alternating'.
 */
export const applyStrum = (events, speed = 0.05, direction = 'down') => {
    // Group events by start time (chords)
    const chords = {};
    events.forEach(ev => {
        const key = Math.round(ev.s * 1000); // 1ms tolerance
        if (!chords[key]) chords[key] = [];
        chords[key].push(ev);
    });

    const strummedEvents = [];
    let strumIndex = 0; // For alternating

    Object.keys(chords).sort((a,b) => Number(a) - Number(b)).forEach(key => {
        const notes = chords[key];

        // If single note, no strum
        if (notes.length < 2) {
            strummedEvents.push(...notes);
            return;
        }

        // Sort notes by pitch
        notes.sort((a, b) => a.d - b.d);

        let currentDir = direction;
        if (direction === 'alternating') {
            currentDir = (strumIndex % 2 === 0) ? 'down' : 'up';
            strumIndex++;
        }

        if (currentDir === 'up') {
            notes.reverse(); // High to Low
        }

        // Apply offsets
        // Higher velocity = faster strum (less delay)
        // Base delay is 'speed'. Scaled by velocity.
        // Vel 1.0 -> 20ms total duration?
        // Vel 0.5 -> 100ms total duration?

        const velocity = notes[0].vel || 0.7;
        const strumDuration = Math.max(0.01, speed * (1.5 - velocity)); // Faster at high vel
        const step = strumDuration / Math.max(1, notes.length - 1);

        notes.forEach((note, i) => {
            const offset = i * step;
            strummedEvents.push({
                ...note,
                s: note.s + offset
            });
        });
    });

    return strummedEvents.sort((a,b) => a.s - b.s);
};

/**
 * Rearranges a keyboard voicing into a playable guitar voicing.
 * Limits to 6 voices, enforces string ranges.
 * @param {Array} notes - MIDI note numbers.
 * @returns {Array} - Guitar-friendly MIDI notes.
 */
export const applyGuitarVoicing = (notes) => {
    if (!notes || notes.length === 0) return notes;

    // 1. Identify Root
    const sorted = [...notes].sort((a,b) => a - b);
    const root = sorted[0];

    // 2. Build Grip (Max 6 notes)
    // We want to favor Open Strings if close: E2(40), A2(45), D3(50), G3(55), B3(59), E4(64)
    // For now, let's just spread the voicing to avoid clusters.

    // Constrain Root to Guitar Range (E2 - E6)
    // Note: E2 is 40.
    let bass = root;
    while (bass < 40) bass += 12; // Bring up to guitar range

    const voiced = [bass];

    // Add chord tones (3rd, 5th, 7th) distributed upwards
    // Remove duplicates (octaves) unless we want double stops
    const pitches = sorted.map(n => n % 12);
    // Unique pitches relative to bass
    const uniquePitches = [...new Set(pitches)].filter(p => p !== (bass % 12));

    // Try to fill strings 2-6
    let currentStringBase = bass; // Imaginary fret position

    uniquePitches.forEach((pc, idx) => {
        if (voiced.length >= 6) return;

        // Find next note higher than last, minimum 3 semitones (except for 2nd interval)
        // Ideally, we want to find a note that fits "under the hand" (within 4 frets of base)
        // OR is an open string.

        let candidate = bass + 1; // Start searching above bass
        while (true) {
            if (candidate % 12 === pc && candidate > voiced[voiced.length-1] + 2) {
                // Found a pitch match. Is it playable?
                // Check stretch: candidate - bass < 12 (approx hand span)?
                // Or check previous note.
                // Simple constraint: Don't jump more than an octave between adjacent strings
                if (candidate - voiced[voiced.length-1] < 16) {
                    voiced.push(candidate);
                    break;
                }
            }
            candidate++;
            if (candidate > bass + 36) break; // Give up
        }
    });

    // Fill remaining with octaves/5ths if sparse (Power Chord reinforcement)
    if (voiced.length < 3) {
        // Add 5th
        const fifth = bass + 7;
        if (!voiced.includes(fifth)) voiced.push(fifth);
        // Add Octave
        const octave = bass + 12;
        if (!voiced.includes(octave)) voiced.push(octave);
    }

    return voiced.sort((a,b) => a - b);
};
